import React, { useState } from 'react';
import { useUser } from '../context/UserContext';
import { Button } from '../components/common';
import { Complexity } from '../types';

const ageGroups = [
  { range: '4-6', label: 'Little Learner', emoji: '\u{1F476}', minAge: 4, maxAge: 6 },
  { range: '7-9', label: 'Explorer', emoji: '\u{1F9D1}\u200D\u{1F3EB}', minAge: 7, maxAge: 9 },
  { range: '10-12', label: 'Champion', emoji: '\u{1F3C6}', minAge: 10, maxAge: 12 },
];

const complexityOptions: { value: Complexity; label: string; description: string; emoji: string }[] = [
  { value: 'basic', label: 'Easy', description: 'Simple words and short sentences', emoji: '\u{1F331}' },
  { value: 'intermediate', label: 'Medium', description: 'Basic sentences and simple stories', emoji: '\u{1F333}' },
  { value: 'advanced', label: 'Hard', description: 'Longer passages and more words', emoji: '\u{1F3C6}' },
];

const OnboardingPage: React.FC = () => {
  const { setAge } = useUser();
  const [selectedAgeGroup, setSelectedAgeGroup] = useState<typeof ageGroups[0] | null>(null);
  const [selectedComplexity, setSelectedComplexity] = useState<Complexity | null>(null);
  const [step, setStep] = useState<'age' | 'complexity'>('age');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleAgeSelect = (group: typeof ageGroups[0]) => {
    setSelectedAgeGroup(group);
    // Set default complexity based on age
    if (group.minAge <= 6) {
      setSelectedComplexity('basic');
    } else if (group.minAge <= 9) {
      setSelectedComplexity('intermediate');
    } else {
      setSelectedComplexity('advanced');
    }
    setStep('complexity');
  };

  const handleComplexitySelect = (complexity: Complexity) => {
    setSelectedComplexity(complexity);
  };

  const handleFinish = async () => {
    if (!selectedAgeGroup || !selectedComplexity) return;

    setLoading(true);
    setError(null);

    try {
      // Use the middle of the age range
      const age = Math.round((selectedAgeGroup.minAge + selectedAgeGroup.maxAge) / 2);
      await setAge(age);
    } catch (err: any) {
      setError(err.message || 'Something went wrong');
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-kids-bg dark:bg-slate-900 flex items-center justify-center p-4">
      <div className="bg-white dark:bg-slate-800 rounded-3xl p-8 max-w-lg w-full shadow-xl dark:shadow-slate-900 relative">
        {step === 'age' ? (
          <>
            {/* Age Selection */}
            <div className="text-center mb-8">
              <div className="text-6xl mb-4">{'\u{1F389}'}</div>
              <h1 className="text-3xl font-bold text-gray-800 dark:text-white">Welcome!</h1>
              <p className="text-gray-600 dark:text-gray-300 mt-2">How old are you?</p>
            </div>

            <div className="space-y-4">
              {ageGroups.map((group) => (
                <button
                  key={group.range}
                  onClick={() => handleAgeSelect(group)}
                  className={`
                    w-full p-6 rounded-2xl border-4 transition-all duration-200
                    hover:scale-105 active:scale-95
                    ${selectedAgeGroup?.range === group.range
                      ? 'border-kids-primary bg-indigo-50 dark:bg-indigo-900/30'
                      : 'border-gray-200 dark:border-slate-600 hover:border-gray-300 dark:hover:border-slate-500'
                    }
                  `}
                >
                  <div className="flex items-center gap-4">
                    <span className="text-4xl">{group.emoji}</span>
                    <div className="text-left">
                      <p className="text-2xl font-bold text-gray-800 dark:text-white">{group.range} years</p>
                      <p className="text-gray-600 dark:text-gray-300">{group.label}</p>
                    </div>
                  </div>
                </button>
              ))}
            </div>
          </>
        ) : (
          <>
            {/* Complexity Selection */}
            <div className="text-center mb-8">
              <button
                onClick={() => setStep('age')}
                className="absolute top-4 left-4 text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200"
              >
                {'<'} Back
              </button>
              <div className="text-6xl mb-4">{'\u{1F4AA}'}</div>
              <h1 className="text-3xl font-bold text-gray-800 dark:text-white">Choose Your Level</h1>
              <p className="text-gray-600 dark:text-gray-300 mt-2">You can change this anytime!</p>
            </div>

            <div className="space-y-4 mb-6">
              {complexityOptions.map((option) => (
                <button
                  key={option.value}
                  onClick={() => handleComplexitySelect(option.value)}
                  className={`
                    w-full p-5 rounded-2xl border-4 transition-all duration-200
                    hover:scale-105 active:scale-95
                    ${selectedComplexity === option.value
                      ? 'border-kids-primary bg-indigo-50 dark:bg-indigo-900/30'
                      : 'border-gray-200 dark:border-slate-600 hover:border-gray-300 dark:hover:border-slate-500'
                    }
                  `}
                >
                  <div className="flex items-center gap-4">
                    <span className="text-3xl">{option.emoji}</span>
                    <div className="text-left">
                      <p className="text-xl font-bold text-gray-800 dark:text-white">{option.label}</p>
                      <p className="text-gray-600 dark:text-gray-300 text-sm">{option.description}</p>
                    </div>
                  </div>
                </button>
              ))}
            </div>

            {error && (
              <div className="bg-red-100 dark:bg-red-900/30 border-2 border-red-300 dark:border-red-700 rounded-xl p-3 text-red-700 dark:text-red-400 text-center mb-4">
                {error}
              </div>
            )}

            <Button
              onClick={handleFinish}
              variant="success"
              size="large"
              fullWidth
              disabled={!selectedComplexity || loading}
            >
              {loading ? 'Setting up...' : "Let's Go!"}
            </Button>
          </>
        )}
      </div>
    </div>
  );
};

export default OnboardingPage;
